/**
 * 图像大小对话框（参照 Photoshop「图像→图像大小」）。
 * 宽高 + 约束比例；缩放是异步的（wasm Lanczos-3 重采样），确定按钮忙时禁用。
 */
import type { JSX } from 'solid-js';
import { Show, createEffect, createSignal } from 'solid-js';
import { useFocusTrap } from '../a11y/focus-trap';
import styles from './ImageSizeDialog.module.css';

export interface ImageSizeDialogProps {
  readonly open: boolean;
  readonly currentWidth: number;
  readonly currentHeight: number;
  readonly busy?: boolean | undefined;
  readonly onClose: () => void;
  readonly onApply: (width: number, height: number) => void;
}

export function ImageSizeDialog(props: ImageSizeDialogProps): JSX.Element {
  const [width, setWidth] = createSignal(props.currentWidth);
  const [height, setHeight] = createSignal(props.currentHeight);
  const [constrain, setConstrain] = createSignal(true);
  let dialogRef: HTMLDivElement | undefined;
  useFocusTrap(() => props.open, () => dialogRef ?? null, props.onClose);

  createEffect(() => {
    if (props.open) {
      setWidth(props.currentWidth);
      setHeight(props.currentHeight);
      setConstrain(true);
    }
  });

  const aspect = (): number => props.currentWidth / props.currentHeight;

  return (
    <Show when={props.open}>
      <div class={styles.backdrop} onClick={props.onClose} data-testid="image-size-backdrop">
        <div
          class={styles.dialog}
          role="dialog"
          ref={(el) => (dialogRef = el)}
          aria-label="图像大小"
          data-testid="image-size-dialog"
          onClick={(e) => e.stopPropagation()}
        >
          <h2 class={styles.title}>图像大小</h2>

          <label class={styles.row}>
            <span class={styles.label}>宽度</span>
            <input
              type="number"
              min="1"
              value={width()}
              data-testid="image-size-width"
              onInput={(e) => {
                const next = Number(e.currentTarget.value) || 1;
                setWidth(next);
                if (constrain()) setHeight(Math.max(1, Math.round(next / aspect())));
              }}
            />
          </label>
          <label class={styles.row}>
            <span class={styles.label}>高度</span>
            <input
              type="number"
              min="1"
              value={height()}
              data-testid="image-size-height"
              onInput={(e) => {
                const next = Number(e.currentTarget.value) || 1;
                setHeight(next);
                if (constrain()) setWidth(Math.max(1, Math.round(next * aspect())));
              }}
            />
          </label>
          <label class={styles.row}>
            <span class={styles.label}>约束比例</span>
            <input
              type="checkbox"
              checked={constrain()}
              data-testid="image-size-constrain"
              onChange={(e) => setConstrain(e.currentTarget.checked)}
            />
          </label>

          <div class={styles.actions}>
            <button type="button" class={styles.secondary} onClick={props.onClose}>
              取消
            </button>
            <button
              type="button"
              class={styles.primary}
              data-testid="image-size-confirm"
              disabled={props.busy === true}
              onClick={() => props.onApply(width(), height())}
            >
              {props.busy === true ? '处理中…' : '确定'}
            </button>
          </div>
        </div>
      </div>
    </Show>
  );
}
